/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import { Button } from 'reactstrap';
import './styles.scss';
import classNames from 'classnames';

/**
 * Briefly the updates:
 *
 * Regarding, Column definitions:
 *  - accessor was renamed to either accessorKey or accessorFn
 *  - lowercase Header, Cell, Footer
 *  - All disable* column options were renamed to enable* column options.
 *    (e.g. disableSortBy is now enableSorting, disableGroupBy is now enableGrouping, etc.)
 *  - sortType was updated to sortingFn
 *
 *  Update details: https://tanstack.com/table/v8/docs/guide/migrating
 */

interface TableProps<T extends object = Record<string, unknown>> {
  defaultSortIcon?: JSX.Element | null;
  columns: ColumnDef<T>[];
  data: T[];
  pageSize?: number;
  className?: string;
  showRowCount?: boolean;
  showHeader?: boolean;
  noData?: string | JSX.Element;
  responsive?: boolean;
  sortBy?: { id: string; desc: boolean }[];
  total?: number;
  unit?: string;
}

export const Table = <T extends object = Record<string, unknown>>(props: TableProps<T>) => {
  const {
    columns,
    data,
    pageSize = 50,
    className = '',
    showRowCount = false,
    showHeader = true,
    responsive = false,
    noData,
    defaultSortIcon = <i className='fa ml-1 fa-blank' />,
    sortBy = [],
    total,
    unit,
  } = props;

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    getPageCount,
    setPageIndex,
    setPageSize,
    getState,
  } = useReactTable({
    columns,
    data,
    enableSortingRemoval: false,
    initialState: {
      pagination: {
        pageSize: pageSize,
      },
      sorting: sortBy,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // TODO: implement sticky columns variant
  });

  const getPageIndex = useCallback(() => getState().pagination.pageIndex, [getState]);

  useEffect(() => {
    // reset current page if the data is out of current view
    if (data.length <= getPageIndex() * pageSize) {
      setPageIndex(0);
    }
    setPageSize(pageSize);
  }, [data, pageSize, setPageSize, getPageIndex, setPageIndex]);

  const changePage = (page: number) => {
    setPageIndex(page);
  };

  if (data.length === 0 && noData) {
    return <div className={classNames('table-component-container', className)}>{noData}</div>;
  }

  return (
    <div
      className={classNames('table-component-container', responsive ? 'table-responsive' : 'table-unresponsive', className)}
    >
      <div className='g17-table'>
        {showHeader ? (
          <div className='g17-thead'>
            {getHeaderGroups().map((headerGroup) => (
              <div className='g17-tr' key={headerGroup.id}>
                {showRowCount && <div className='g17-tr rowCountCol'></div>}
                {headerGroup.headers.map((header) => (
                  <div
                    {...{
                      key: header.id,
                      className: classNames('g17-th', { 'cursor-pointer select-none': header.column.getCanSort() }),
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                    <span>
                      {{
                        asc: <i className='fa ml-1 fa-chevron-down' />,
                        desc: <i className='fa ml-1 fa-chevron-up' />,
                      }[header.column.getIsSorted() as string] ?? defaultSortIcon}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <></>
        )}
        <div className='g17-tbody'>
          {getRowModel().rows.map((row, i) => {
            return (
              <div className='g17-tr' key={row.id}>
                {showRowCount && <div className='g17-td rowCountCol'>{`${getPageIndex() * pageSize + i + 1}.`}</div>}
                {row.getVisibleCells().map((cell) => {
                  return (
                    <div className='g17-td' key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className='pagination align-items-center mt-3'>
        {getPageCount() > 1 && (
          <div>
            <Button size='sm' className='mr-1' onClick={() => changePage(0)} disabled={!getCanPreviousPage()}>
              <i className='fa fa-fast-backward' />
            </Button>
            <Button
              size='sm'
              className='mr-1'
              onClick={() => changePage(getPageIndex() - 1)}
              disabled={!getCanPreviousPage()}
            >
              <i className='fa fa-caret-left' />
            </Button>
            <span>{`Page ${getPageIndex() + 1} of ${getPageOptions().length}`}</span>
            <Button
              size='sm'
              className='ml-1'
              onClick={() => changePage(getPageIndex() + 1)}
              disabled={!getCanNextPage()}
            >
              <i className='fa fa-caret-right' />
            </Button>
            <Button
              size='sm'
              className='ml-1'
              onClick={() => changePage(getPageCount() - 1)}
              disabled={!getCanNextPage()}
            >
              <i className='fa fa-fast-forward' />
            </Button>
          </div>
        )}
        {data.length > 0 && total ? (
          <p className='text-right position-absolute end-0 pr-3 text-xs text-ThemeTextMedium'>
            {data.length} / {total} {unit || ''}
          </p>
        ) : null}
      </div>
    </div>
  );
};
